import { useEffect, useMemo, useRef, useState } from 'react'
import { Send, MessagesSquare } from 'lucide-react'
import { cn } from '../../lib/cn'
import { useAuth } from '../../contexts/AuthContext'
import { useMessages } from '../../hooks/useMessages'
import type { ChatMessage, GroupMember } from '../../lib/types'
import { EmptyState } from '../ui'

function formatTime(iso: string | null): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

function Bubble({ msg, own, authorName }: { msg: ChatMessage; own: boolean; authorName: string | null }) {
  const isSystem = msg.kind === 'system'
  if (isSystem) {
    // muted, not faint: system lines are real events (WCAG 1.4.3 on sunken).
    return <p className="text-center text-xs text-text-muted">{msg.body}</p>
  }
  return (
    <div className={cn('flex', own ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-prose rounded-card p-3',
          own ? 'bg-ember-900/40 ring-1 ring-border-warm' : 'bg-surface ring-1 ring-border',
        )}
      >
        {/* Attribution per bubble (§6.6) — a group stream without authors is
            ambiguous. Rendered only when the roster can resolve the id. */}
        {authorName && <p className="text-xs text-text-muted">{authorName}</p>}
        <p className={cn('text-sm leading-relaxed text-text', authorName && 'mt-0.5')}>{msg.body}</p>
        {/* muted, not §6.6's literal faint: a timestamp is content, and faint
            sits under 4.5:1 on these bubble fills (WCAG 1.4.3). */}
        <p className="mt-1 font-mono text-xs tabular-nums text-text-muted">{formatTime(msg.created_at)}</p>
      </div>
    </div>
  )
}

interface GroupChatProps {
  groupId: string
  /** Group roster for bubble attribution (§6.6 author names) — pass `group.members`. */
  members?: GroupMember[]
}

export function GroupChat({ groupId, members }: GroupChatProps) {
  const { user } = useAuth()
  const { messages, available, send } = useMessages(groupId)
  const [draft, setDraft] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)

  // author_user_id → display name, from the roster the caller already has
  // (plus the signed-in user, so own bubbles resolve even off-roster).
  const nameByUserId = useMemo(() => {
    const map = new Map<string, string>()
    for (const m of members ?? []) {
      if (m.display_name) map.set(m.user_id, m.display_name)
    }
    if (user?.id && user.display_name && !map.has(user.id)) map.set(user.id, user.display_name)
    return map
  }, [members, user])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
  }, [messages])

  const submit = () => {
    if (!draft.trim()) return
    send(draft)
    setDraft('')
  }

  return (
    <section className="flex flex-col gap-3">
      <h2 className="font-display text-xl text-text-strong">Around the fire</h2>

      {/* role="log" (WCAG 4.1.3): new messages — including recommendation
          drops, the feedback capture point (§6.6) — announce politely to AT. */}
      <div
        ref={scrollRef}
        role="log"
        aria-live="polite"
        aria-label="Group messages"
        className="max-h-96 space-y-3 overflow-y-auto rounded-card bg-surface-sunken p-4"
      >
        {!available ? (
          <EmptyState
            icon={<MessagesSquare className="h-8 w-8" strokeWidth={1.5} />}
            title="Chat is warming up"
            description="The group message stream isn't wired in this build yet."
          />
        ) : messages.length === 0 ? (
          <p className="py-6 text-center text-sm text-text-muted">No messages yet — say hi.</p>
        ) : (
          messages.map((m) => (
            <Bubble
              key={m.id}
              msg={m}
              own={m.author_user_id === user?.id}
              authorName={m.author_user_id ? (nameByUserId.get(m.author_user_id) ?? null) : null}
            />
          ))
        )}
      </div>

      {available && (
        <div className="flex items-end gap-2 rounded-control bg-surface p-2 ring-1 ring-border">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                submit()
              }
            }}
            rows={1}
            aria-label="Message the group"
            placeholder="Message the group…"
            className="flex-1 resize-none bg-transparent px-2 py-1.5 text-sm text-text placeholder:text-text-faint focus:outline-none"
          />
          <button
            type="button"
            onClick={submit}
            disabled={!draft.trim()}
            aria-label="Send message"
            className={cn(
              // ≥44×44 effective target (§7) — same rule as the feedback thumbs.
              'inline-flex h-11 w-11 items-center justify-center rounded-control transition',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember-400 focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
              draft.trim() ? 'text-ember-400 hover:bg-surface-raised' : 'text-text-faint',
            )}
          >
            <Send className="h-4.5 w-4.5" strokeWidth={1.75} />
          </button>
        </div>
      )}
    </section>
  )
}
